import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

import { useAuthStore } from '../stores/auth-store'
import i18n from '../i18n'

const appTitle = '飛網世界網卡專賣店後台'

const getLocalizedTitle = (titleKey?: unknown) => {
  if (typeof titleKey !== 'string') {
    return appTitle
  }

  return `${i18n.global.t(titleKey)} - ${appTitle}`
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'dashboard' },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        meta: { titleKey: 'menu.dashboard' },
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        meta: { titleKey: 'menu.settings' },
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        meta: { titleKey: 'menu.preferences' },
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'users',
        path: 'users',
        meta: { titleKey: 'menu.users', requiresAdmin: true },
        component: () => import('../pages/users/UsersPage.vue'),
      },
      {
        name: 'product-mappings',
        path: 'product-mappings',
        meta: { titleKey: 'menu.product-mappings', requiresAdmin: true },
        component: () => import('../pages/product-mappings/ProductMappingsPage.vue'),
      },
      {
        name: 'orders',
        path: 'orders',
        meta: { titleKey: 'menu.orders' },
        component: () => import('../pages/orders/OrdersPage.vue'),
      },
      {
        name: 'products',
        path: 'products',
        meta: { titleKey: 'menu.products' },
        component: () => import('../pages/products/ProductsPage.vue'),
      },
      {
        name: 'email-template',
        path: 'email-template',
        meta: { titleKey: 'menu.email-template', requiresAdmin: true },
        component: () => import('../pages/email-template/index.vue'),
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        meta: { titleKey: 'auth.login' },
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        meta: { titleKey: 'auth.sign_up' },
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        meta: { titleKey: 'auth.recover_password' },
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        meta: { titleKey: 'auth.recover_password' },
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    meta: { titleKey: '404.title' },
    component: () => import('../pages/404.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()

  // 定義需要驗證的路由
  const requiresAuth = !['login', 'signup', 'recover-password'].includes(to.name as string)

  if (requiresAuth && !authStore.isAuthenticated) {
    // 未登入且訪問需要驗證的頁面，重定向到登入頁
    next({ name: 'login' })
  } else if (authStore.isAuthenticated && ['login', 'signup'].includes(to.name as string)) {
    // 已登入卻訪問登入/註冊頁，重定向到首頁
    next({ name: 'dashboard' })
  } else if (to.matched.some((route) => route.meta.requiresAdmin) && !authStore.user?.permissions?.isAdmin) {
    next({ name: 'dashboard' })
  } else {
    next()
  }
})

router.afterEach((to) => {
  document.title = getLocalizedTitle(to.meta.titleKey)
})

export default router
